import {
  FormDialogFormFields,
  type FormDialogProps,
  FormDialogSubmitButton,
  FormDialogSubtitle,
  FormDialogTitle,
  FormDialogTrigger,
  FormDialog as _FormDialog,
} from './form-dialog';

/**
 * A dialog component that wraps a form and provides a trigger, title, subtitle, form fields, and a submit button.
 * This component uses `react-hook-form` to manage the form state and validation.
 *
 * @param props - The props for the form dialog.
 * @returns A dialog component that wraps a form and provides a trigger, title, subtitle, form fields, and a submit button.
 *
 * @example
 * ```tsx
 * const formMethods = useForm<FormSchema>();
 *
 * return (
 * <FormDialog
 *   formMethods={formMethods}
 *   formName="test-dialog"
 *   onSubmit={handleSubmit(onSubmit)}
 *   onResetForm={handleResetForm}
 * >
 *  <FormDialog.Trigger>
 *    <Button>Open Dialog</Button>
 *  </FormDialog.Trigger>
 *  <FormDialog.Title>Test Dialog</FormDialog.Title>
 *  <FormDialog.Subtitle>This is a test dialog</FormDialog.Subtitle>
 *  <FormDialog.FormFields>
 *    <FormTextField name="name" label="Name" isRequired />
 *    <FormTextField name="email" label="Email" isRequired />
 *  </FormDialog.FormFields>
 *  <FormDialog.SubmitButton>Submit</FormDialog.SubmitButton>
 * </FormDialog>
 * );
 * ```
 */
const FormDialog = Object.assign(_FormDialog, {
  /**
   * A trigger component that opens the dialog.
   *
   * @param props - The props for the trigger component.
   * @returns A trigger component that opens the dialog.
   *
   * @example
   * ```tsx
   * <FormDialog.Trigger>
   *  <Button>Open Dialog</Button>
   * </FormDialog.Trigger>
   * ```
   */
  Trigger: FormDialogTrigger,
  /**
   * A title component that displays the title of the dialog.
   *
   * @param props - The props for the title component.
   * @returns A title component that displays the title of the dialog.
   *
   * @example
   * ```tsx
   * <FormDialog.Title>Test Dialog</FormDialog.Title>
   * ```
   */
  Title: FormDialogTitle,
  /**
   * A subtitle component that displays the subtitle of the dialog.
   *
   * @param props - The props for the subtitle component.
   * @returns A subtitle component that displays the subtitle of the dialog.
   *
   * @example
   * ```tsx
   * <FormDialog.Subtitle>This is a test dialog</FormDialog.Subtitle>
   * ```
   */
  Subtitle: FormDialogSubtitle,
  /**
   * A form fields component that displays the form fields of the dialog.
   *
   * @param props - The props for the form fields component.
   * @returns A form fields component that displays the form fields of the dialog.
   *
   * @example
   * ```tsx
   * <FormDialog.FormFields>
   *  <FormTextField name="name" label="Name" isRequired />
   *  <FormTextField name="email" label="Email" isRequired />
   * </FormDialog.FormFields>
   * ```
   */
  FormFields: FormDialogFormFields,
  /**
   * A submit button component that submits the form.
   *
   * @param props - The props for the submit button component.
   * @returns A submit button component that submits the form.
   *
   * @example
   * ```tsx
   * <FormDialog.SubmitButton>Submit</FormDialog.SubmitButton>
   * ```
   */
  SubmitButton: FormDialogSubmitButton,
});

export { FormDialog };
export type { FormDialogProps };
